/*  ========================================================================
    # Sections - Contact Person Slider
    ========================================================================  */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import tailwindConfig from '../../../../tailwind.config';
import useLocalization from '@/react/hooks/useLocalization';
import useTranslation from '@/react/hooks/useTranslation';
import bem from '@/react/utils/bem';
import LazyImage from '@/react/components/LazyImage';
import LazySource from '@/react/components/LazySource';

const ContactPersonSlider = ({ headline, entries }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const __ = useTranslation();

  // Icons
  const localize = useLocalization();
  const iconChevronLeft = `${localize('svgSprite')}#angle-left`;
  const iconChevronRight = `${localize('svgSprite')}#angle-right`;
  const iconMail = `${localize('svgSprite')}#mail`;
  const iconPhone = `${localize('svgSprite')}#phone`;

  // If there are fewer than 5 entries, the array is padded with existing ones.
  const sliderEntries = useMemo(() => {
    let tempEntries = [];

    if (entries.length > 3 && entries.length < 5) {
      tempEntries = [...entries];
      while (tempEntries.length < 5) tempEntries.push(...entries);
    } else {
      tempEntries = entries;
    }

    return tempEntries;
  }, [entries]);

  // Get the slide offset value.
  const getSlideOffset = index => {
    const delta = index - activeSlide;

    if (activeSlide === 0 && index === sliderEntries.length - 1) return -1;
    if (activeSlide === 0 && index === sliderEntries.length - 2) return -2;
    if (activeSlide === 1 && index === sliderEntries.length - 1) return -2;

    if (activeSlide === sliderEntries.length - 1 && index === 0) return 1;
    if (activeSlide === sliderEntries.length - 1 && index === 1) return 2;

    if (activeSlide === sliderEntries.length - 2 && index === 0) return 2;

    if (delta <= -3) return -2;
    if (delta >= 3) return 2;

    return delta;
  };

  const getMode = () => {
    if (sliderEntries.length > 4) {
      return "multiple";
    }
    else if (sliderEntries.length == 3) {
      return "three";
    }
  }

  // Navigate Left
  const navigateLeft = () => {
    if (activeSlide > 0) setActiveSlide(activeSlide - 1);
    else setActiveSlide(sliderEntries.length - 1);
  };

  // Navigate Right
  const navigateRight = () => {
    if (activeSlide < sliderEntries.length - 1) setActiveSlide(activeSlide + 1);
    else setActiveSlide(0);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: navigateRight,
    onSwipedRight: navigateLeft
  });


  return (
    <>
      <div className="container">
        {headline &&
          <div className="s-contact-person-slider__headline wysiwyg-block"
            dangerouslySetInnerHTML={{ __html: headline }}>
          </div>}
        <div className="s-contact-person-slider__container">
          {sliderEntries.length > 3 && <button
            className='s-contact-person-slider__quote-control s-contact-person-slider__quote-control--left c-button c-button--icon'
            title={__('section-testimonials-navigate-left')}
            aria-label={__('section-testimonials-navigate-left')}
            onClick={navigateLeft}
          >
            <svg viewBox='0 0 200 200'>
              <use href={iconChevronLeft} />
            </svg>
          </button>
          }
          <div className={bem('s-contact-person-slider', 'entries', { slide: sliderEntries.length > 3, noslide: sliderEntries.length < 4 })}
            style={{ '--slide-count': entries.length, '--active-slide': activeSlide }}>
            {sliderEntries.map((entry, index) => (
              <div
                key={`${entry.name}-${index}`}
                className='s-contact-person-slider__entry'
                data-offset={sliderEntries.length === 1 ? '' : getSlideOffset(index)}
              >
                <LazyImage
                  figureClass={'s-contact-person-slider__entry-image'}
                  defaultUrl={entry.image.sizes['medium_large']}
                  alt={entry.image.alt}
                  placeholderUrl={entry.image.sizes['placeholder']}
                  dataOffset={0}
                >
                  <LazySource srcSet={entry.image.large} mediaMin={tailwindConfig.theme.screens.md} />
                  <LazySource srcSet={entry.image.medium_large} mediaMin={tailwindConfig.theme.screens.sm} />
                </LazyImage>
                <div className="s-contact-person-slider__entry-info-container">
                  <h4 dangerouslySetInnerHTML={{ __html: entry.infos.name }} />
                  <div className="s-contact-person-slider__entry-position h6" dangerouslySetInnerHTML={{ __html: entry.infos.position }}></div>
                  <a className="s-contact-person-slider__entry-mail-container" href={`mailto:${entry.infos.mail}`}>
                    <svg viewBox="0 0 24 24">
                      <use href={iconMail} />
                    </svg>
                    <span dangerouslySetInnerHTML={{ __html: entry.infos.mail }} />
                  </a>
                  {entry.infos.phone ?
                    <a className="s-contact-person-slider__entry-phone-container" href={`tel:${entry.infos.phone}`}>
                      <svg viewBox="0 0 24 24">
                        <use href={iconPhone} />
                      </svg>
                      <span dangerouslySetInnerHTML={{ __html: entry.infos.phone }} />
                    </a>
                    : null}
                </div>
              </div>
            ))}
          </div>
          {sliderEntries.length > 3 && <button
            className='s-contact-person-slider__quote-control s-contact-person-slider__quote-control--left c-button c-button--icon'
            title={__('section-testimonials-navigate-left')}
            aria-label={__('section-testimonials-navigate-left')}
            onClick={navigateRight}
          >
            <svg viewBox='0 0 200 200'>
              <use href={iconChevronRight} />
            </svg>
          </button>
          }
        </div>
      </div >
    </>
  );
};

ContactPersonSlider.propTypes = {
  headline: PropTypes.string,
  entries: PropTypes.array
};

export default ContactPersonSlider;
